import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaBlog, FaBriefcase, FaEnvelope, FaHistory, FaProjectDiagram } from 'react-icons/fa';
import BlogManager from '../components/admin/BlogManager';
import ChangelogManager from '../components/admin/ChangelogManager';
import MessageManager from '../components/admin/MessageManager';
import ProjectManager from '../components/admin/ProjectManager';
import TimelineManager from '../components/admin/TimelineManager';
import supabase from '../supabase';
import './Admin.css';

function Admin() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('projects');
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data, error }) => {
      if (!active) return;
      if (error) setLoginError('Unable to check your session. Please sign in again.');
      setSession(data?.session || null);
      setLoading(false);
    }).catch(() => { if (active) setLoading(false); });
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (active) setSession(nextSession);
    });
    return () => { active = false; data?.subscription?.unsubscribe(); };
  }, []);

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        setLoginError(error.message);
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('An error occurred during login. Please try again.');
    }
  };

  // Logout handler
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      // Navigate to homepage after logout
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Note: All authenticated users are considered admins for now
  // Additional role checks could be implemented here if needed

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  // If not logged in, show login form
  if (!session) {
    return (
      <div className="admin-container">
        <h1>Admin Login</h1>
        <Link className="admin-return" to="/">← Back to portfolio</Link>
        
        <form className="admin-login-form" onSubmit={handleLogin}>
          {loginError && <p className="login-error">{loginError}</p>}
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
          
          <button type="submit" className="login-btn">Login</button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-sidebar">
        <div className="admin-logo">Admin Panel</div>
        <nav className="admin-nav" aria-label="Admin sections">
          <button 
            className={`admin-nav-item ${activeTab === 'projects' ? 'active' : ''}`}
            aria-pressed={activeTab === 'projects'}
            onClick={() => setActiveTab('projects')}
          >
            <FaProjectDiagram /> Projects
          </button>
          <button
            className={`admin-nav-item ${activeTab === 'timeline' ? 'active' : ''}`}
            aria-pressed={activeTab === 'timeline'}
            onClick={() => setActiveTab('timeline')}
          >
            <FaBriefcase /> Timeline
          </button>
          <button
            className={`admin-nav-item ${activeTab === 'blog' ? 'active' : ''}`}
            aria-pressed={activeTab === 'blog'}
            onClick={() => setActiveTab('blog')}
          >
            <FaBlog /> Blog Posts
          </button>
          <button 
            className={`admin-nav-item ${activeTab === 'changelog' ? 'active' : ''}`}
            aria-pressed={activeTab === 'changelog'}
            onClick={() => setActiveTab('changelog')}
          >
            <FaHistory /> Changelog
          </button>
          <button 
            className={`admin-nav-item ${activeTab === 'messages' ? 'active' : ''}`}
            aria-pressed={activeTab === 'messages'}
            onClick={() => setActiveTab('messages')}
          >
            <FaEnvelope /> Message archive
          </button>
        </nav>
        <div className="admin-sidebar-footer">
          <Link className="admin-return" to="/">← View portfolio</Link>
          <button className="admin-logout" onClick={handleLogout}>
            Sign Out
          </button>
        </div>
      </div>
      
      <div className="admin-content">
        <header className="admin-header">
          <h1>
            {activeTab === 'projects' && 'Manage Projects'}
            {activeTab === 'timeline' && 'Manage Timeline'}
            {activeTab === 'blog' && 'Manage Blog Posts'}
            {activeTab === 'changelog' && 'Manage Changelog'}
            {activeTab === 'messages' && 'Message archive'}
          </h1>
          <div className="admin-user">
            {session.user.email}
          </div>
        </header>
        
        <div className="admin-main">
          {activeTab === 'projects' && <ProjectManager />}
          {activeTab === 'timeline' && <TimelineManager />}
          {activeTab === 'blog' && <BlogManager />}
          {activeTab === 'changelog' && <ChangelogManager />}
          {activeTab === 'messages' && <MessageManager />}
        </div>
      </div>
    </div>
  );
}

export default Admin;
